/**
 * PropBatcher — every copy of a prop in one draw call.
 *
 * ── The number that forced this ──────────────────────────────────────────
 *
 * Furnishing the arenas took them from ~60 meshes to 2,200. Each prop is a
 * Group of boxes, so a floor of 130 desks and chairs is 130 Groups and roughly
 * a thousand draw calls that all draw the SAME nine objects. That is fine on a
 * desktop GPU and it is not shippable to the phone this game is played on.
 *
 * ── How it collapses ─────────────────────────────────────────────────────
 *
 * A prop kind is built ONCE, then its meshes are baked down: each child's local
 * transform is applied into its vertices and children sharing a material are
 * merged into a single geometry. A desk becomes about four geometries — one per
 * material it uses — rather than nine meshes. Those become `InstancedMesh`es,
 * and every desk on the map is one more matrix in a buffer.
 *
 * So the cost stops scaling with how many props an arena has and starts scaling
 * with how many KINDS it uses: nine kinds at ~4 materials each is under forty
 * draw calls whether the floor holds ten desks or a thousand.
 *
 * ── Why not the existing InstancedBatchManager ───────────────────────────
 *
 * That one instances PLATFORMS: one unit box, one material, per-instance colour
 * and a scale baked into the matrix. A prop is multi-material and multi-part,
 * and the pieces are not uniform boxes. Same idea, different unit of work —
 * trying to serve both from one class would have meant a batch that is
 * sometimes a box and sometimes a bag of merged geometries.
 */

import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { buildProp } from './TradingFloorProps.js';

/** kind -> [{ geometry, material }], baked once per session. */
const _parts = new Map();

/** kind -> Matrix4[] accumulated between `beginProps` and `flushProps`. */
let _pending = new Map();

/** The InstancedMeshes currently in the scene, so `clearProps` can drop them. */
let _live = [];

const _matrix = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);
const _up = new THREE.Vector3(0, 1, 0);

/**
 * Bakes one prop kind down to a geometry per material.
 *
 * `applyMatrix4` on a clone rather than on the source geometry: the box
 * geometries a prop builds are its own, but baking in place would mean the
 * second call to this function bakes an already-baked geometry and every prop
 * of that kind drifts a little further from where it belongs.
 */
function partsFor(kind) {
  const hit = _parts.get(kind);
  if (hit) return hit;

  const group = buildProp(kind);
  if (!group) return null;
  group.updateMatrixWorld(true);

  const byMaterial = new Map();
  group.traverse((o) => {
    if (!o.isMesh) return;
    const geo = o.geometry.clone();
    geo.applyMatrix4(o.matrixWorld);
    const list = byMaterial.get(o.material) ?? [];
    list.push(geo);
    byMaterial.set(o.material, list);
  });

  const parts = [];
  for (const [material, geos] of byMaterial) {
    // `mergeGeometries` returns null when the inputs disagree on attributes.
    // One box among the group's boxes without UVs would take the whole prop
    // down, so fall back to drawing the pieces separately rather than dropping
    // the prop — a slow desk beats a missing desk.
    const merged = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
    if (merged) {
      parts.push({ geometry: merged, material });
    } else {
      for (const geometry of geos) parts.push({ geometry, material });
    }
  }

  _parts.set(kind, parts);
  return parts;
}

/** Starts a new collection pass. Call once per arena build. */
export function beginProps() {
  _pending = new Map();
}

/**
 * Queues one placement. Nothing is drawn until `flushProps`.
 *
 * @param {string} kind
 * @param {number} x @param {number} y base of the prop @param {number} z
 * @param {number} rotY radians
 */
export function addProp(kind, x, y, z, rotY = 0) {
  if (!partsFor(kind)) return false;
  const list = _pending.get(kind) ?? [];
  _quat.setFromAxisAngle(_up, rotY);
  _pos.set(x, y, z);
  list.push(new THREE.Matrix4().compose(_pos, _quat, _one));
  _pending.set(kind, list);
  return true;
}

/** Builds the InstancedMeshes for everything queued and parents them to `root`. */
export function flushProps(root) {
  for (const [kind, matrices] of _pending) {
    const parts = partsFor(kind);
    if (!parts) continue;
    for (const { geometry, material } of parts) {
      const mesh = new THREE.InstancedMesh(geometry, material, matrices.length);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      // These never move after a build, so telling three not to re-upload the
      // matrix buffer every frame is free.
      mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
      for (let i = 0; i < matrices.length; i++) {
        _matrix.copy(matrices[i]);
        mesh.setMatrixAt(i, _matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.name = `props:${kind}`;
      root.add(mesh);
      _live.push(mesh);
    }
  }
  _pending = new Map();
}

/**
 * Removes the batches from the scene.
 *
 * Geometries and materials are NOT disposed: both are the per-kind bake, shared
 * by every arena for the life of the session, and freeing them here would leave
 * the next arena's InstancedMeshes pointing at dead buffers. `disposePropParts`
 * is the one that actually releases them.
 */
export function clearProps(root) {
  for (const mesh of _live) {
    root.remove(mesh);
    mesh.dispose();
  }
  _live = [];
  _pending = new Map();
}

/** Frees the baked geometries. For teardown, not for an arena change. */
export function disposePropParts() {
  for (const parts of _parts.values()) {
    for (const part of parts) part.geometry.dispose();
  }
  _parts.clear();
}
